import React, { useState, useRef, useEffect } from "react";
import { Stack } from "@phosphor-icons/react";

/**
 * Iter 19.63.4 — Kleines Badge, das anzeigt, wenn eine Zeile aus mehreren
 * Original-SV-Auszug-Zeilen zusammengefasst wurde. Klick öffnet ein Popover
 * mit der Liste der Original-Sub-Perioden.
 *
 * Props:
 *   sourceItems: Array<{ datum_von, datum_bis, arbeitgeber, taetigkeit, anrechenbar, anrechnungsfaktor }>
 */
const _fmtDate = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const lc = iso.toLowerCase();
  if (lc === "laufend" || lc === "offen") return "laufend";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
};

export default function KonsolidiertBadge({ sourceItems }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!Array.isArray(sourceItems) || sourceItems.length < 2) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="konsolidiert-badge-btn"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium text-warm-600 bg-warm-100 hover:bg-warm-200 border border-warm-300 rounded-sm transition-colors"
        title={`Aus ${sourceItems.length} Einzel-Perioden zusammengefasst`}
      >
        <Stack size={10} weight="duotone" />
        aus {sourceItems.length} Zeilen
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 right-0 w-[340px] bg-warm-50 border border-warm-300 shadow-xl rounded-sm p-3"
          data-testid="konsolidiert-popover"
        >
          <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-600 mb-2">
            Original-Perioden im SV-Auszug
          </div>
          <p className="text-[11px] text-warm-700 leading-snug mb-2">
            Diese Zeile wurde aus {sourceItems.length} aufeinanderfolgenden
            Einzelperioden mit identischem Arbeitgeber, Tätigkeit und
            Anrechenbarkeit zusammengefasst (Lücke ≤ 1 Tag).
          </p>
          <ul className="space-y-1.5 text-[12px]">
            {sourceItems.map((s, i) => (
              <li
                key={i}
                className="flex items-start gap-2 border-b border-warm-200 pb-1.5 last:border-b-0 last:pb-0"
                data-testid={`konsolidiert-source-${i}`}
              >
                <span className="text-warm-400 tabular-nums text-[10px] mt-0.5">
                  {i + 1}.
                </span>
                <span className="tabular-nums">
                  {_fmtDate(s.datum_von)} – {_fmtDate(s.datum_bis)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
